import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHotel } from "@/hooks/useHotel";
import { useServiceUpdate } from "./hooks/useServiceUpdate";
import { useHotelService } from "@/hooks/useHotelServices";
import HotelServiceForm from "./components/ServiceForm";
import { CreateHotelServiceFormData } from "./utils/serviceSchema";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { mapServiceToFormData } from "./utils/mapServiceToUpdateFormData";
import { HotelService } from "@/types/hotelService";
import { useTranslation } from "react-i18next";

export const ServicePage = () => {
  const { hotelId, isLoading, error } = useHotel();

  if (isLoading) {
    return <div>Loading</div>;
  }

  if (error) {
    <div className="py-4 text-center text-red-600">
      Error loading hotel: {error.message}
    </div>;
  }

  return <Service hotelId={hotelId!} />;
};

const Service = ({ hotelId }: { hotelId: string }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useHotelService(id!, hotelId);
  const { handleHotelServiceUpdate, isLoading: isUpdating } = useServiceUpdate(
    id!,
    hotelId,
  );

  const service = useMemo(
    () => (data ? mapServiceToFormData(data as HotelService) : null),
    [data],
  );

  const handleUpdate = async (
    data: CreateHotelServiceFormData,
    file: File | null,
  ) => {
    await handleHotelServiceUpdate(data, file);
    navigate("/services");
  };

  if (isLoading || !service) return <div>Loading...</div>;

  return (
    <div className="relative flex min-h-screen flex-col p-6">
      <Button
        type="button"
        variant="new_outline"
        size="sm"
        onClick={() => navigate("/services")}
        className="absolute top-12 left-12"
      >
        <ArrowLeft />
        {t("services.form.actions.back", "Back")}
      </Button>
      <HotelServiceForm
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
        initialValues={service}
        submitButtonText={t("services.form.actions.update", "Update Service")}
        titleText={t("services.form.editTitle", "Edit Service")}
      />
    </div>
  );
};

export default ServicePage;
